import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, RefreshCw, Eye } from "lucide-react";

const roleOptions = [
  { value: "retail", label: "Customer" },
  { value: "reseller", label: "Reseller" },
  { value: "agen", label: "Agen" },
  { value: "mitra", label: "Mitra" },
  { value: "distributor", label: "Distributor" },
];

const pricingRuleOptions = [
  { value: "role_then_campaign", label: "Tambah diskon setelah harga role" },
  { value: "campaign_final_discount", label: "Override diskon normal" },
  { value: "campaign_final_price", label: "Harga final campaign" },
];

const targetTypeOptions = [
  { value: "all", label: "Semua produk" },
  { value: "stock_type", label: "Berdasarkan stock type" },
  { value: "brand", label: "Brand tertentu" },
  { value: "category", label: "Kategori tertentu" },
  { value: "product", label: "Produk tertentu" },
  { value: "variant", label: "Varian tertentu" },
];

const discountTypeOptions = [
  { value: "percent", label: "Persentase (%)" },
  { value: "fixed", label: "Nominal potongan (Rp)" },
  { value: "final_price", label: "Harga final campaign (Rp)" },
];

const createEmptyRoleRule = () => ({
  role: "retail",
  discount_type: "percent",
  discount_value: 0,
  min_margin_percent: 0,
  min_margin_amount: 0,
});

const createEmptyTarget = () => ({
  target_type: "all",
  target_value: "",
  product_id: "",
  variant_id: "",
});

const emptyForm = {
  name: "",
  description: "",
  status: "draft",
  start_at: "",
  end_at: "",
  pricing_rule: "role_then_campaign",
  priority: 0,
  stackable: false,
  role_rules: [createEmptyRoleRule()],
  targets: [createEmptyTarget()],
};

function rupiah(value) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function normalizeDateTimeInput(value) {
  if (!value) return "";
  const text = String(value).trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text)) return text;
  const match = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  return match ? `${match[1]}T${match[2]}` : "";
}

function buildVariantLabel(variant = {}) {
  const parts = [variant.style, variant.color, variant.size].filter(Boolean);
  return parts.length ? parts.join(" / ") : variant.variant_id || "Varian default";
}

export default function AdminDiscounts() {
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [products]
  );

  const load = async () => {
    try {
      const [campaignRes, productRes, brandRes, categoryRes] = await Promise.all([
        API.get("/admin/campaigns"),
        API.get("/products?limit=200&summary=false"),
        API.get("/brands?limit=500"),
        API.get("/categories"),
      ]);
      setCampaigns(campaignRes.data?.campaigns || []);
      setProducts(productRes.data?.products || []);
      setBrands(brandRes.data?.brands || []);
      setCategories(categoryRes.data?.categories || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || err.message) || "Gagal memuat campaign");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateRoleRule = (idx, key, value) => {
    setForm((prev) => ({
      ...prev,
      role_rules: prev.role_rules.map((r, i) => (i === idx ? { ...r, [key]: value } : r)),
    }));
  };

  const updateTarget = (idx, key, value) => {
    setForm((prev) => ({
      ...prev,
      targets: prev.targets.map((t, i) => {
        if (i !== idx) return t;
        const next = { ...t, [key]: value };
        if (key === "target_type") {
          if (value === "all") return createEmptyTarget();
          if (value === "stock_type") return { ...createEmptyTarget(), target_type: value, target_value: "ready_stock" };
          return { ...createEmptyTarget(), target_type: value };
        }
        if (key === "product_id") {
          next.variant_id = "";
          if (next.target_type === "product") next.target_value = value;
        }
        if (key === "variant_id") {
          next.target_value = value;
        }
        if (key === "target_value" && next.target_type === "product") {
          next.product_id = value;
        }
        return next;
      }),
    }));
  };

  const getProductById = (productId) => sortedProducts.find((p) => p.id === productId);
  const getVariantsForProduct = (productId) => getProductById(productId)?.variants || [];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const normalizedTargets = form.targets.map((t) => {
        const target = { ...t };
        if (target.target_type === "product") {
          target.target_value = target.product_id || target.target_value || "";
        }
        if (target.target_type === "variant") {
          target.target_value = target.variant_id || target.target_value || "";
        }
        if (["brand", "category", "stock_type"].includes(target.target_type)) {
          target.product_id = "";
          target.variant_id = "";
        }
        if (target.target_type === "all") {
          return createEmptyTarget();
        }
        return target;
      });

      const payload = {
        ...form,
        priority: Number(form.priority || 0),
        role_rules: form.role_rules.map((r) => ({
          ...r,
          discount_value: Number(r.discount_value || 0),
          min_margin_percent: Number(r.min_margin_percent || 0),
          min_margin_amount: Number(r.min_margin_amount || 0),
        })),
        targets: normalizedTargets,
      };

      const res = editingId
        ? await API.put(`/admin/campaigns/${editingId}`, payload)
        : await API.post("/admin/campaigns", payload);

      toast.success(editingId ? "Campaign diperbarui" : "Campaign dibuat");
      setEditingId(res.data?.id || editingId);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || err.message) || "Gagal menyimpan campaign");
    } finally {
      setLoading(false);
    }
  };

  const edit = (c) => {
    setEditingId(c.id);
    setPreview(null);
    setForm({
      name: c.name || "",
      description: c.description || "",
      status: c.status || "draft",
      start_at: normalizeDateTimeInput(c.start_at),
      end_at: normalizeDateTimeInput(c.end_at),
      pricing_rule: c.pricing_rule || "role_then_campaign",
      priority: c.priority || 0,
      stackable: !!c.stackable,
      role_rules: c.role_rules?.length ? c.role_rules : [createEmptyRoleRule()],
      targets: c.targets?.length ? c.targets : [createEmptyTarget()],
    });
  };

  const doPreview = async (id = editingId) => {
    if (!id) return toast.error("Simpan campaign dulu sebelum preview");
    try {
      const { data } = await API.get(`/admin/campaigns/${id}/preview`);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || err.message) || "Gagal preview campaign");
    }
  };

  const startNewCampaign = () => {
    setEditingId("");
    setForm({
      ...emptyForm,
      role_rules: [createEmptyRoleRule()],
      targets: [createEmptyTarget()],
    });
    setPreview(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderTargetInputs = (target, idx) => {
    const variants = getVariantsForProduct(target.product_id);

    if (target.target_type === "all") {
      return (
        <div className="rounded-lg bg-stone-50 border border-dashed border-stone-200 px-3 py-3 text-sm text-stone-500">
          Campaign ini akan berlaku ke semua produk yang cocok dengan role rule.
        </div>
      );
    }

    if (target.target_type === "stock_type") {
      return (
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Stock type</label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={target.target_value || "ready_stock"}
              onChange={(e) => updateTarget(idx, "target_value", e.target.value)}
            >
              <option value="ready_stock">Ready Stock</option>
              <option value="preorder">PreOrder</option>
            </select>
          </div>
        </div>
      );
    }

    if (target.target_type === "brand") {
      return (
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Pilih brand</label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={target.target_value || ""}
            onChange={(e) => updateTarget(idx, "target_value", e.target.value)}
          >
            <option value="">Pilih brand campaign</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (target.target_type === "category") {
      return (
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Pilih kategori</label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={target.target_value || ""}
            onChange={(e) => updateTarget(idx, "target_value", e.target.value)}
          >
            <option value="">Pilih kategori campaign</option>
            {categories.map((category) => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (target.target_type === "product") {
      return (
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Pilih produk</label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={target.product_id || target.target_value || ""}
            onChange={(e) => updateTarget(idx, "product_id", e.target.value)}
          >
            <option value="">Pilih produk campaign</option>
            {sortedProducts.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name} {product.brand_name ? `• ${product.brand_name}` : ""}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-stone-500">Campaign berlaku ke seluruh varian dari produk yang dipilih.</p>
        </div>
      );
    }

    if (target.target_type === "variant") {
      return (
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Pilih produk</label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={target.product_id || ""}
              onChange={(e) => updateTarget(idx, "product_id", e.target.value)}
            >
              <option value="">Pilih produk lebih dulu</option>
              {sortedProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} {product.brand_name ? `• ${product.brand_name}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Pilih varian</label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={target.variant_id || target.target_value || ""}
              onChange={(e) => updateTarget(idx, "variant_id", e.target.value)}
              disabled={!target.product_id}
            >
              <option value="">{target.product_id ? "Pilih varian campaign" : "Pilih produk dulu"}</option>
              {variants.map((variant) => (
                <option key={variant.variant_id} value={variant.variant_id}>
                  {buildVariantLabel(variant)}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">Campaign Discount</h1>
            <p className="text-sm text-stone-500">
              Atur diskon campaign per role, lalu pilih target campaign dengan pilihan yang lebih jelas.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={startNewCampaign} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-600 text-white">
              + Tambah Campaign
            </button>
            <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-100 text-stone-700">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-white rounded-2xl border border-stone-200 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold text-stone-800">Daftar Campaign</h2>
              <button type="button" onClick={startNewCampaign} className="text-sm font-medium text-purple-600">+ Baru</button>
            </div>
            {campaigns.map((c) => (
              <button
                key={c.id}
                onClick={() => edit(c)}
                className={`w-full text-left p-3 rounded-xl border ${editingId === c.id ? "border-purple-300 bg-purple-50" : "border-stone-200 bg-white"}`}
              >
                <div className="font-medium text-stone-800">{c.name}</div>
                <div className="text-xs text-stone-500">{c.status} · {c.pricing_rule}</div>
                <div className="text-xs text-stone-400">Roles: {(c.role_rules || []).map((r) => r.role).join(", ") || "-"}</div>
              </button>
            ))}
            {!campaigns.length && <p className="text-sm text-stone-500">Belum ada campaign.</p>}
          </div>

          <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl border border-stone-200 p-4 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-stone-800">{editingId ? "Edit Campaign" : "Campaign Baru"}</h2>
              <button
                type="button"
                onClick={startNewCampaign}
                className="text-sm text-purple-600"
              >
                + Tambah Campaign Baru
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Nama campaign</label>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Contoh: Promo Lebaran Reseller"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Status campaign</label>
                <select className="w-full border rounded-lg px-3 py-2" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-stone-700 mb-1">Rule perhitungan harga</label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.pricing_rule}
                  onChange={(e) => {
                    const pricingRule = e.target.value;
                    setForm({
                      ...form,
                      pricing_rule: pricingRule,
                      role_rules: pricingRule === "campaign_final_price"
                        ? form.role_rules.map((rule) => ({ ...rule, discount_type: "final_price" }))
                        : form.role_rules,
                    });
                  }}
                >
                  {pricingRuleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Mulai aktif (WIB)</label>
                <input
                  type="datetime-local"
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.start_at}
                  onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Berakhir (WIB)</label>
                <input
                  type="datetime-local"
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.end_at}
                  onChange={(e) => setForm({ ...form, end_at: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Prioritas</label>
                <input
                  type="number"
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Semakin besar, semakin diutamakan"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2 pt-7">
                <input
                  id="stackable"
                  type="checkbox"
                  checked={!!form.stackable}
                  onChange={(e) => setForm({ ...form, stackable: e.target.checked })}
                />
                <label htmlFor="stackable" className="text-sm text-stone-700">Boleh stack dengan campaign lain</label>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-stone-700 mb-1">Deskripsi campaign</label>
                <textarea
                  className="w-full border rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="Catatan internal campaign, misalnya tujuan promo atau periode khusus"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>

            <div className="border rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-stone-800">Role Rules</h3>
                  <p className="text-xs text-stone-500">Tentukan diskon campaign untuk masing-masing role.</p>
                </div>
                <button type="button" onClick={() => setForm({ ...form, role_rules: [...form.role_rules, createEmptyRoleRule()] })} className="text-sm text-purple-600">
                  + Role
                </button>
              </div>

              {form.role_rules.map((r, idx) => (
                <div key={idx} className="rounded-xl border border-stone-200 p-3 space-y-3">
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">Role</label>
                      <select className="w-full border rounded-lg px-3 py-2" value={r.role} onChange={(e) => updateRoleRule(idx, "role", e.target.value)}>
                        {roleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">{form.pricing_rule === "campaign_final_price" ? "Mode harga" : "Tipe diskon"}</label>
                      <select
                        className="w-full border rounded-lg px-3 py-2"
                        value={form.pricing_rule === "campaign_final_price" ? "final_price" : r.discount_type}
                        disabled={form.pricing_rule === "campaign_final_price"}
                        onChange={(e) => updateRoleRule(idx, "discount_type", e.target.value)}
                      >
                        {(form.pricing_rule === "campaign_final_price" ? discountTypeOptions.filter((o) => o.value === "final_price") : discountTypeOptions).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">{form.pricing_rule === "campaign_final_price" ? "Harga final campaign" : "Nilai diskon"}</label>
                      <input
                        className="w-full border rounded-lg px-3 py-2"
                        type="number"
                        value={r.discount_value}
                        onChange={(e) => updateRoleRule(idx, "discount_value", e.target.value)}
                        placeholder={r.discount_type === "percent" ? "Contoh: 10" : "Contoh: 25000"}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button type="button" onClick={() => setForm({ ...form, role_rules: form.role_rules.filter((_, i) => i !== idx) })} className="text-red-600 text-sm">
                      Hapus role rule
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-stone-800">Target Campaign</h3>
                  <p className="text-xs text-stone-500">Pilih produk mana yang akan terkena campaign. Tidak perlu isi ID manual untuk alur normal.</p>
                </div>
                <button type="button" onClick={() => setForm({ ...form, targets: [...form.targets, createEmptyTarget()] })} className="text-sm text-purple-600">
                  + Target
                </button>
              </div>

              {form.targets.map((t, idx) => (
                <div key={idx} className="rounded-xl border border-stone-200 p-3 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Tipe target</label>
                    <select className="w-full border rounded-lg px-3 py-2" value={t.target_type} onChange={(e) => updateTarget(idx, "target_type", e.target.value)}>
                      {targetTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {renderTargetInputs(t, idx)}

                  <div className="flex justify-end">
                    <button type="button" onClick={() => setForm({ ...form, targets: form.targets.filter((_, i) => i !== idx) })} className="text-red-600 text-sm">
                      Hapus target
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <button disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white disabled:opacity-60">
                <Save className="w-4 h-4" /> Simpan
              </button>
              <button type="button" onClick={() => doPreview()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-100 text-amber-700">
                <Eye className="w-4 h-4" /> Preview
              </button>
            </div>
          </form>
        </div>

        {preview && (
          <div className="bg-white rounded-2xl border border-stone-200 p-4 overflow-x-auto">
            <h2 className="font-semibold mb-3">Preview Harga ({preview.total_previewed})</h2>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-stone-500 border-b">
                  <th className="py-2">Produk</th>
                  <th>Varian</th>
                  <th>Role</th>
                  <th>Base</th>
                  <th>Role Price</th>
                  <th>Final</th>
                  <th>Margin</th>
                  <th>Warning</th>
                </tr>
              </thead>
              <tbody>
                {(preview.items || []).map((it, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="py-2">{it.product_name}</td>
                    <td>{it.variant_label}</td>
                    <td>{it.role}</td>
                    <td>{rupiah(it.base_price)}</td>
                    <td>{rupiah(it.role_price)}</td>
                    <td className="font-semibold">{rupiah(it.final_price)}</td>
                    <td>{rupiah(it.margin_amount)} ({it.margin_percent}%)</td>
                    <td>{it.margin_warning ? <span className="text-red-600 font-medium">Low margin</span> : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
